/**
 * Small bits shared by the TikTok workspace cards, rail, preview and review.
 */
import React from 'react';
import clsx from 'clsx';
import type { WorkspaceAd, WorkspaceAdGroup } from '@/contexts/TiktokWorkspaceContext';

export const inputCls =
  'w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary disabled:opacity-60 disabled:cursor-not-allowed';

export const OBJECTIVE_LABELS: Record<string, string> = {
  TRAFFIC:         'Traffic',
  CONVERSIONS:     'Conversions',
  REACH:           'Reach',
  VIDEO_VIEWS:     'Video views',
  ENGAGEMENT:      'Engagement',
  LEAD_GENERATION: 'Lead generation',
  PRODUCT_SALES:   'Product sales',
  APP_PROMOTION:   'App promotion',
};

export const CTA_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'LEARN_MORE',   label: 'Learn more' },
  { value: 'SHOP_NOW',     label: 'Shop now' },
  { value: 'SIGN_UP',      label: 'Sign up' },
  { value: 'DOWNLOAD_NOW', label: 'Download' },
  { value: 'CONTACT_US',   label: 'Contact us' },
  { value: 'BOOK_NOW',     label: 'Book now' },
  { value: 'APPLY_NOW',    label: 'Apply now' },
  { value: 'WATCH_NOW',    label: 'Watch now' },
];

export const ctaLabel = (value?: string) =>
  CTA_OPTIONS.find((c) => c.value === value)?.label
  ?? (value || 'LEARN_MORE').toLowerCase().split('_').map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(' ');

export const AD_TEXT_MAX = 100;

export type Identity = { identityId: string; identityType: string; displayName?: string; profileImage?: string };

/** Transcoding gate persisted on the ad's formData so the review step can read it. */
export type VideoGate =
  | { state: 'idle' }
  | { state: 'processing'; width?: number; height?: number }
  | { state: 'ready'; width?: number; height?: number }
  | { state: 'rejected'; reason: string };

export const adForm = (ad: WorkspaceAd | null | undefined) => (ad?.formData ?? {}) as Record<string, unknown>;
export const groupForm = (g: WorkspaceAdGroup | null | undefined) => (g?.formData ?? {}) as Record<string, unknown>;

export const groupPlacementsOf = (g: WorkspaceAdGroup | null | undefined): string[] => {
  const arr = groupForm(g).placements;
  const list = Array.isArray(arr) ? (arr as string[]) : ['PLACEMENT_TIKTOK'];
  return list.length ? list : ['PLACEMENT_TIKTOK'];
};

/**
 * Identity compatibility is placement-dependent (live-verified): TikTok
 * in-feed rejects CUSTOMIZED_USER ("Custom identities are no longer
 * supported"), while Pangle / Global App Bundle reject account identities
 * ("Incorrect source field").
 */
export const identityFitsPlacement = (identityType: string, tiktokPlacement: boolean) => (tiktokPlacement
  ? identityType === 'TT_USER' || identityType === 'BC_AUTH_TT' || identityType === 'AUTH_CODE'
  : identityType === 'CUSTOMIZED_USER');

export const TikTokGlyph = ({ size = 15, className }: { size?: number; className?: string }) => (
  <svg viewBox="0 0 48 48" width={size} height={size} aria-hidden className={className}>
    <path fill="currentColor" d="M31.1 8.7h-5.5v22.1a4.66 4.66 0 0 1-4.65 4.5 4.66 4.66 0 0 1-2.1-8.8v-5.7A10.3 10.3 0 0 0 10.6 31a10.35 10.35 0 0 0 10.35 10.3c5.72 0 10.35-4.6 10.35-10.3V19.5a13.6 13.6 0 0 0 7.5 2.25v-5.6a8.6 8.6 0 0 1-7.7-7.45z" />
  </svg>
);

export const Pill = ({ tone, children, className }: { tone: 'ok' | 'warn' | 'bad' | 'muted' | 'gold'; children: React.ReactNode; className?: string }) => (
  <span className={clsx(
    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold',
    tone === 'ok' && 'bg-success-soft text-success',
    tone === 'warn' && 'bg-warning-soft text-warning',
    tone === 'bad' && 'bg-danger-soft text-danger',
    tone === 'muted' && 'bg-surface-alt text-text-muted',
    tone === 'gold' && 'bg-primary-soft text-primary',
    className,
  )}>
    {children}
  </span>
);

export const errMsg = (e: unknown, fallback = 'Something went wrong.'): string => {
  const r = e as { response?: { data?: { message?: string | string[] } }; message?: string } | undefined;
  const m = r?.response?.data?.message;
  if (Array.isArray(m)) return m.join(' ');
  return m || r?.message || fallback;
};

/** True when the global axios interceptor already showed a toast for this error. */
export const alreadyToasted = (e: unknown) => !!(e as { response?: unknown } | undefined)?.response;

export const fmtDuration = (sec?: number) => {
  if (!sec || !Number.isFinite(sec)) return null;
  const s = Math.round(sec);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
